import React, { useEffect, useState, useMemo } from "react";
import { Button, Modal, Form, Badge } from "react-bootstrap";
import CommonTable from "../components/CommonTable";

const API_URL = `${process.env.REACT_APP_API_URL}/api/news`;
const IMAGE_URL = `${process.env.REACT_APP_API_URL}/uploads/`;

const ManageNews = () => {
    const [newsList, setNewsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchNews();
    }, []);

    // ✅ IMAGE
    const getImage = (item) => {
        if (!item) return "https://via.placeholder.com/100x60?text=No+Image";

        if (item.image && item.image.startsWith("http")) return item.image;
        if (item.imagePath && item.imagePath.startsWith("http")) return item.imagePath;
        if (item.image) return `${IMAGE_URL}${item.image}`;

        return "https://via.placeholder.com/100x60?text=No+Image";
    };

    const getAdImage = (item) => {
        if (!item) return "https://via.placeholder.com/100x60?text=No+Ad";

        if (item.adImage && item.adImage.startsWith("http")) return item.adImage;
        if (item.adImage) return `${IMAGE_URL}${item.adImage}`;

        return "https://via.placeholder.com/100x60?text=No+Ad";
    };

    // ✅ FETCH
    const fetchNews = async () => {
        try {
            setLoading(true);
            const res = await fetch(API_URL);
            const result = await res.json();
            setNewsList(result?.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ DELETE
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this news?")) return;
        await fetch(`${API_URL}/${id}`, { method: "DELETE" });
        fetchNews();
    };

    // ✅ EDIT
    const handleEdit = (row) => {
        setSelectedNews(row.original);
        setShowEdit(true);
    };

    // ✅ SAVE
    const handleSave = async () => {
        try {
            setSaving(true);

            const formData = new FormData();
            formData.append("title", selectedNews.title);
            formData.append("category", selectedNews.category);
            formData.append("writer", selectedNews.writer);
            formData.append("shortDesc", selectedNews.shortDesc);
            formData.append("content", selectedNews.content);
            formData.append("videoUrl", selectedNews.videoUrl);
            formData.append("status", selectedNews.status);

            if (selectedNews.newImage)
                formData.append("image", selectedNews.newImage);

            if (selectedNews.newAdImage)
                formData.append("adImage", selectedNews.newAdImage);

            await fetch(`${API_URL}/${selectedNews._id}`, {
                method: "PUT",
                body: formData,
            });

            setShowEdit(false);
            fetchNews();
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    // ✅ TABLE
    const columns = useMemo(
        () => [
            { accessorKey: "title", header: "Title" },
            { accessorKey: "writer", header: "Writer" },
            { accessorKey: "category", header: "Category" },

            // SHORT DESC
            {
                accessorKey: "shortDesc",
                header: "Short Desc",
                Cell: ({ cell }) => (
                    <span>{cell.getValue()?.slice(0, 40)}...</span>
                ),
            },

            // FULL CONTENT
            {
                accessorKey: "content",
                header: "Full Content",
                Cell: ({ cell }) => (
                    <div style={{ maxWidth: "250px", fontSize: "13px" }}>
                        {cell.getValue()?.slice(0, 80)}...
                    </div>
                ),
            },

            // VIDEO
            {
                header: "Video",
                Cell: ({ row }) =>
                    row.original.videoUrl ? (
                        <a href={row.original.videoUrl} target="_blank" rel="noreferrer">
                            ▶ Watch
                        </a>
                    ) : (
                        "-"
                    ),
            },

            // IMAGE
            {
                header: "Image",
                Cell: ({ row }) => (
                    <img
                        src={getImage(row.original)}
                        alt=""
                        style={{ width: "80px", height: "50px", objectFit: "cover" }}
                    />
                ),
            },

            // AD IMAGE
            {
                header: "Ad",
                Cell: ({ row }) => (
                    <img
                        src={getAdImage(row.original)}
                        alt=""
                        style={{ width: "80px", height: "50px", objectFit: "cover" }}
                    />
                ),
            },

            // STATUS
            {
                header: "Status",
                accessorKey: "status",
                Cell: ({ cell }) => (
                    <Badge bg={cell.getValue() === "Published" ? "success" : "secondary"}>
                        {cell.getValue()}
                    </Badge>
                ),
            },

            // ACTIONS
            {
                header: "Actions",
                Cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <Button size="sm" onClick={() => handleEdit(row)}>
                            Edit
                        </Button>
                        <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDelete(row.original._id)}
                        >
                            Delete
                        </Button>
                    </div>
                ),
            },
        ],
        []
    );

    return (
        <div className="container-fluid">
            <h4 className="mb-4 fw-bold">Manage News</h4>

            <CommonTable columns={columns} data={newsList} isLoading={loading} />

            {/* MODAL */}
            <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit News</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            <Form.Control
                                className="mb-2"
                                value={selectedNews.title || ""}
                                onChange={(e) =>
                                    setSelectedNews({ ...selectedNews, title: e.target.value })
                                }
                            />

                            <Form.Control
                                className="mb-2"
                                value={selectedNews.writer || ""}
                                onChange={(e) =>
                                    setSelectedNews({ ...selectedNews, writer: e.target.value })
                                }
                            />

                            <Form.Control
                                className="mb-2"
                                value={selectedNews.category || ""}
                                onChange={(e) =>
                                    setSelectedNews({ ...selectedNews, category: e.target.value })
                                }
                            />

                            {/* SHORT DESC */}
                            <Form.Control
                                className="mb-2"
                                placeholder="Short Description"
                                value={selectedNews.shortDesc || ""}
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        shortDesc: e.target.value,
                                    })
                                }
                            />

                            {/* FULL CONTENT */}
                            <Form.Control
                                as="textarea"
                                rows={4}
                                className="mb-2"
                                placeholder="Full Content"
                                value={selectedNews.content || ""}
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        content: e.target.value,
                                    })
                                }
                            />

                            {/* FULL CONTENT PREVIEW */}
                            <div className="p-2 bg-light border rounded mb-2">
                                <strong>Preview:</strong>
                                <p style={{ whiteSpace: "pre-line", fontSize: "14px" }}>
                                    {selectedNews.content}
                                </p>
                            </div>

                            {/* VIDEO */}
                            <Form.Control
                                className="mb-2"
                                placeholder="Video URL"
                                value={selectedNews.videoUrl || ""}
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        videoUrl: e.target.value,
                                    })
                                }
                            />

                            {/* IMAGE */}
                            <img
                                src={getImage(selectedNews)}
                                style={{ width: "100%", height: "200px", objectFit: "cover" }}
                                alt=""
                            />

                            <Form.Control
                                type="file"
                                className="my-2"
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        newImage: e.target.files[0],
                                    })
                                }
                            />

                            {/* AD IMAGE */}
                            <img
                                src={getAdImage(selectedNews)}
                                style={{ width: "100%", height: "150px", objectFit: "cover" }}
                                alt=""
                            />

                            <Form.Control
                                type="file"
                                className="my-2"
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        newAdImage: e.target.files[0],
                                    })
                                }
                            />

                            <Form.Select
                                value={selectedNews.status || "Published"}
                                onChange={(e) =>
                                    setSelectedNews({
                                        ...selectedNews,
                                        status: e.target.value,
                                    })
                                }
                            >
                                <option value="Published">Published</option>
                                <option value="Draft">Draft</option>
                            </Form.Select>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={() => setShowEdit(false)}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManageNews;